import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";

import { getActiveNavIndex, NAV_LINKS } from "./navOrder";

interface NavLinksProps {
  compact?: boolean;
}

interface IndicatorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function NavLinks({ compact = false }: NavLinksProps) {
  const navRef = useRef<HTMLElement>(null);
  const linkRefs = useRef<Map<number, HTMLElement>>(new Map());
  const location = useLocation();

  const activeIndex = getActiveNavIndex(location.pathname);

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [pressedIndex, setPressedIndex] = useState<number | null>(null);
  const [indicatorRect, setIndicatorRect] = useState<IndicatorRect | null>(
    null,
  );

  const targetIndex = hoveredIndex ?? (activeIndex >= 0 ? activeIndex : null);
  const isVisible = targetIndex !== null;

  const measureTarget = useCallback((index: number | null) => {
    if (index === null) return;
    const el = linkRefs.current.get(index);
    const container = navRef.current;
    if (!el || !container) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const insetY = 8; // 0.5rem — vertical inset to keep indicator at original padding distance
    const insetX = 8; // 0.25rem — horizontal inset for x-axis spacing
    setIndicatorRect({
      top: eRect.top - cRect.top + insetY,
      left: eRect.left - cRect.left + insetX,
      width: eRect.width - insetX * 2,
      height: eRect.height - insetY * 2,
    });
  }, []);

  useEffect(() => {
    if (targetIndex !== null) {
      measureTarget(targetIndex);
      return;
    }
    // No target — let fade-out play, then unmount the indicator
    if (activeIndex < 0) {
      const timer = setTimeout(() => setIndicatorRect(null), 500);
      return () => clearTimeout(timer);
    }
  }, [targetIndex, activeIndex, measureTarget]);

  useEffect(() => {
    const container = navRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      measureTarget(targetIndex);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [targetIndex, measureTarget]);

  const handleMouseDown = useCallback((i: number) => {
    setPressedIndex(i);
    const cleanup = () => {
      setPressedIndex(null);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("dragend", onDragEnd);
    };
    const onMouseUp = () => cleanup();
    const onDragEnd = () => cleanup();
    document.addEventListener("mouseup", onMouseUp, { once: true });
    document.addEventListener("dragend", onDragEnd, { once: true });
  }, []);

  const className = compact
    ? "flex flex-col gap-0 mt-10 tracking-widest items-center order-1"
    : "flex flex-row gap-0 mt-6 tracking-widest lg:items-center lg:flex-col lg:mt-10 lg:order-1";

  return (
    <nav ref={navRef} className={`relative ${className}`}>
      {indicatorRect && (
        <div
          className="pointer-events-none absolute -z-1"
          style={{
            top: indicatorRect.top,
            left: indicatorRect.left,
            width: indicatorRect.width,
            height: indicatorRect.height,
            border: "1px solid",
            borderColor: "var(--color-nav-dark)",
            backgroundColor:
              pressedIndex !== null && pressedIndex === targetIndex
                ? "var(--color-nav-dark)"
                : "transparent",
            opacity: isVisible ? 1 : 0,
            transition:
              "top 500ms, left 500ms, width 500ms, height 500ms, opacity 500ms, background-color 150ms ease-out",
            transitionTimingFunction:
              "linear(0, 0.029 1.6%, 0.123 3.5%, 0.651 10.6%, 0.862 14.1%, 1.002 17.7%, 1.046 19.6%, 1.074 21.6%, 1.087 23.9%, 1.086 26.6%, 1.014 38.5%, 0.994 46.3%, 1)",
          }}
        />
      )}

      {NAV_LINKS.map((link, i) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === "/"}
          ref={(el: HTMLAnchorElement | null) => {
            if (el) linkRefs.current.set(i, el);
            else linkRefs.current.delete(i);
          }}
          className={() => {
            const isActive = activeIndex === i;
            return `header-nav-link ${isActive && (hoveredIndex === null || hoveredIndex === i) ? "active" : ""} ${pressedIndex === i ? "pressed" : ""}`;
          }}
          onMouseDown={() => handleMouseDown(i)}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}
